import { useEffect, useState } from 'react';
import { Box, Stack, Typography, createFilterOptions } from '@mui/material';
import { IdentityAvatar, FormAutocomplete, ValueBadge, type FieldControlState } from '../../design-system';

export type WorkflowPersonOption = {
  personId: string;
  workerId: string;
  name: string;
  title: string;
  organizationName?: string;
  profilePhotoUrl?: string;
};

export type WorkflowRoleOption = {
  roleCode: string;
  descriptor: string;
};

type ResourceState = {
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  state?: FieldControlState;
  invalid?: boolean;
};

export type WorkflowOrganizationOption = { id: string; name: string; parentId?: string };

function organizationPath(option: WorkflowOrganizationOption, options: readonly WorkflowOrganizationOption[]): string {
  const names = [option.name];
  const seen = new Set([option.id]);
  let parent = options.find((candidate) => candidate.id === option.parentId);
  while (parent && !seen.has(parent.id)) {
    seen.add(parent.id);
    names.unshift(parent.name);
    parent = options.find((candidate) => candidate.id === parent?.parentId);
  }
  return names.join(' / ');
}

export function WorkflowOrganizationPicker({ label, organizationId, options, onChange, disabled, required = true, loading, error, onRetry, state, invalid }: ResourceState & {
  label: string;
  organizationId: string;
  options: readonly WorkflowOrganizationOption[];
  onChange: (id: string) => void;
  disabled?: boolean;
  required?: boolean;
}) {
  const [query, setQuery] = useState('');
  const selected = options.find((option) => option.id === organizationId) ?? null;
  return <FormAutocomplete
    id={state?.controlId} options={[...options]} value={selected} disabled={disabled}
    inputValue={selected ? organizationPath(selected, options) : query}
    onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setQuery(value); onChange(''); } }}
    selectionError={organizationId && !selected && !loading && !error ? '선택한 조직을 찾을 수 없습니다. 다시 선택해 주세요.' : undefined}
    request={error ? { status: 'error', message: error, onRetry } : loading ? { status: 'loading' } : { status: 'ready' }}
    getOptionKey={(option) => option.id}
    getOptionLabel={(option) => organizationPath(option, options)}
    isOptionEqualToValue={(option, value) => option.id === value.id}
    onChange={(_, option) => { setQuery(''); onChange(option?.id ?? ''); }}
    noOptionsText="검색 결과가 없습니다." openText="조직 검색 열기" clearText="선택 해제"
    input={{ label: state ? '' : label, hideLabel: Boolean(state), required, placeholder: '조직 이름 검색', error: invalid, ariaLabelledBy: state?.labelId, ariaDescribedBy: state?.messageId }}
  />;
}

type WorkflowPersonPickerProps = ResourceState & {
  label: string;
  personId: string;
  options: readonly WorkflowPersonOption[];
  disabled?: boolean;
  required?: boolean;
  onChange: (personId: string) => void;
};

type WorkflowRolePickerProps = ResourceState & {
  label: string;
  roleCode: string;
  options: readonly WorkflowRoleOption[];
  disabled?: boolean;
  required?: boolean;
  loadOptions?: (query: string) => Promise<WorkflowRoleOption[]>;
  onChange: (roleCode: string) => void;
};

const personFilter = createFilterOptions<WorkflowPersonOption>({
  stringify: (option) => `${option.name} ${option.title} ${option.organizationName ?? ''}`,
});

const roleFilter = createFilterOptions<WorkflowRoleOption>({
  stringify: (option) => `${option.descriptor} ${option.roleCode}`,
});

function personInitials(name: string): string {
  return Array.from(name.trim()).slice(0, 2).join('') || '?';
}

/** 구성원 참조 ID를 직접 노출하지 않고, 임직원 디렉터리의 공개 Person 참조를 선택합니다. */
export function WorkflowPersonPicker({
  label,
  personId,
  options,
  disabled = false,
  required = true,
  loading = false,
  error,
  onRetry,
  onChange,
  state,
  invalid,
}: WorkflowPersonPickerProps) {
  const [query, setQuery] = useState('');
  const selected = options.find((option) => option.personId === personId) ?? null;
  const unresolved = Boolean(personId && !selected && !loading && !error);

  return <FormAutocomplete
    id={state?.controlId}
    options={[...options]}
    value={selected}
    inputValue={selected ? selected.name : query}
    onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setQuery(value); onChange(''); } }}
    selectionError={unresolved ? '선택한 항목을 찾을 수 없습니다. 다시 검색해 선택해 주세요.' : undefined}
    disabled={disabled}
    request={error ? { status: 'error', message: error, onRetry } : loading ? { status: 'loading' } : { status: 'ready' }}
    filterOptions={personFilter}
    getOptionKey={(option) => option.personId}
    getOptionLabel={(option) => option.name}
    isOptionEqualToValue={(option, value) => option.personId === value.personId}
    noOptionsText="검색 결과가 없습니다."
    loadingText="구성원을 불러오는 중…"
    clearText="선택 해제"
    openText="구성원 검색 열기"
    onChange={(_, option) => { setQuery(''); onChange(option?.personId ?? ''); }}
    renderOption={(props, option) => {
      const { key, ...optionProps } = props;
      return <Box component="li" key={key} {...optionProps}>
        <IdentityAvatar
          src={option.profilePhotoUrl}
          alt=""
          sx={{ width: 34, height: 34, mr: 1.25, fontSize: 12, fontWeight: 800 }}
        >
          {personInitials(option.name)}
        </IdentityAvatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 750 }}>{option.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[option.title, option.organizationName].filter(Boolean).join(' · ') || '직책·소속 미등록'}
          </Typography>
        </Box>
      </Box>;
    }}
    input={{
      required,
      label: state ? '' : label, hideLabel: Boolean(state),
      error: invalid, ariaLabelledBy: state?.labelId, ariaDescribedBy: state?.messageId,
      placeholder: '이름, 직책 또는 조직 검색',

      startAdornment: selected ? <IdentityAvatar
        src={selected.profilePhotoUrl}
        alt=""
        sx={{ width: 24, height: 24, mr: 0.75, fontSize: 10, fontWeight: 800 }}
      >
        {personInitials(selected.name)}
      </IdentityAvatar> : undefined,
    }}
  />;
}

/** 보안 roleCode는 저장값으로만 유지하고 사용자는 역할 설명을 검색·선택합니다. */
export function WorkflowRolePicker({
  label,
  roleCode,
  options,
  disabled = false,
  required = true,
  loadOptions,
  loading = false,
  error,
  onRetry,
  onChange,
}: WorkflowRolePickerProps) {
  const [query, setQuery] = useState('');
  const [remote, setRemote] = useState<WorkflowRoleOption[]>([]);
  const [resolved, setResolved] = useState<WorkflowRoleOption | null>(null);
  const [resolvedKey, setResolvedKey] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string>();
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!loadOptions) return;
    let cancelled = false;
    setSearchLoading(true);
    setSearchError(undefined);
    const timer = setTimeout(() => {
      void loadOptions(query).then((items) => {
        if (!cancelled) setRemote(items);
      }).catch((cause: unknown) => {
        if (!cancelled) setSearchError(cause instanceof Error ? cause.message : '역할을 검색하지 못했습니다.');
      }).finally(() => { if (!cancelled) setSearchLoading(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [loadOptions, query, retry]);
  useEffect(() => {
    if (!loadOptions || !roleCode) return;
    let cancelled = false;
    void loadOptions(roleCode).then((items) => {
      if (!cancelled) {
        setResolved(items.find((item) => item.roleCode === roleCode) ?? null);
        setResolvedKey(roleCode);
      }
    }).catch((cause: unknown) => {
      if (!cancelled) setSearchError(cause instanceof Error ? cause.message : '선택한 역할을 확인하지 못했습니다.');
    });
    return () => { cancelled = true; };
  }, [loadOptions, roleCode, retry]);
  const selected = [...remote, ...options].find((option) => option.roleCode === roleCode)
    ?? (resolved?.roleCode === roleCode ? resolved : null);
  const activeLoading = loadOptions ? searchLoading || Boolean(roleCode && !selected && resolvedKey !== roleCode) : loading;
  const activeError = loadOptions ? searchError : error;
  const unresolved = Boolean(roleCode && !selected && !activeLoading && !activeError);

  return <FormAutocomplete
    options={loadOptions ? remote : [...options]}
    value={selected}
    inputValue={selected ? '' : query}
    selectionError={unresolved ? '선택한 항목을 찾을 수 없습니다. 다시 검색해 선택해 주세요.' : undefined}
    disabled={disabled}
    request={activeError ? { status: 'error', message: activeError, onRetry: loadOptions ? () => setRetry((value) => value + 1) : onRetry } : activeLoading ? { status: 'loading' } : { status: 'ready' }}
    filterOptions={loadOptions ? (items) => items : roleFilter}
    onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setQuery(value); onChange(''); } }}
    getOptionKey={(option) => option.roleCode}
    getOptionLabel={(option) => option.descriptor}
    isOptionEqualToValue={(option, value) => option.roleCode === value.roleCode}
    noOptionsText="검색 결과가 없습니다."
    loadingText="승인 역할을 불러오는 중…"
    renderValue={(option, getItemProps) => {
      const { onDelete: _onDelete, ...itemProps } = getItemProps();
      return <Box component="span" {...itemProps} role="group" aria-label={`${label} 선택값`} sx={{ maxWidth: '100%', minWidth: 0 }}><ValueBadge label={option.descriptor} /></Box>;
    }}
    clearText="선택 해제"
    openText="승인 역할 검색 열기"
    onChange={(_, option) => { setQuery(''); onChange(option?.roleCode ?? ''); }}
    renderOption={(props, option) => {
      const { key, ...optionProps } = props;
      return <Box component="li" key={key} {...optionProps}>
        <Stack spacing={0.15} sx={{ minWidth: 0 }}>
          <ValueBadge label={option.descriptor} />
          <Typography variant="caption" color="text.secondary">역할</Typography>
        </Stack>
      </Box>;
    }}
    input={{
      required,
      label,
      placeholder: '역할 이름 검색',

    }}
  />;
}
